'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { Flame } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface Supporter {
  user_id: string;
  display_name: string;
  support_count: number;
}

interface TopSupportersProps {
  postId: string;
  refreshKey?: number;
}

export default function TopSupporters({ postId, refreshKey = 0 }: TopSupportersProps) {
  const [supporters, setSupporters] = useState<Supporter[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchTopSupporters = useCallback(async () => {
    try {
      const { data: txData, error: txError } = await supabase
        .from('transactions')
        .select('user_id')
        .eq('post_id', postId)
        .eq('transaction_type', 'support');

      if (txError || !txData || txData.length === 0) {
        setSupporters([]);
        return;
      }

      const countMap: Record<string, number> = {};
      for (const row of txData) {
        countMap[row.user_id] = (countMap[row.user_id] ?? 0) + 1;
      }

      const top3 = Object.entries(countMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);

      if (top3.length === 0) {
        setSupporters([]);
        return;
      }

      const userIds = top3.map(([uid]) => uid);

      const { data: profiles } = await supabase
        .from('user_profiles')
        .select('id, display_name, email')
        .in('id', userIds);

      const profileMap: Record<string, string> = {};
      for (const p of profiles ?? []) {
        profileMap[p.id] = p.display_name || p.email?.split('@')[0] || 'Anonymous';
      }

      const result: Supporter[] = top3.map(([uid, count]) => ({
        user_id: uid,
        display_name: profileMap[uid] ?? 'Anonymous',
        support_count: count * 10,
      }));

      setSupporters(result);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchTopSupporters();
  }, [fetchTopSupporters, refreshKey]);

  useEffect(() => {
    const channel = supabase
      .channel(`top-supporters-${postId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions',
          filter: `post_id=eq.${postId}`,
        },
        () => {
          fetchTopSupporters();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [postId, fetchTopSupporters]);

  if (loading) return null;
  if (supporters.length === 0) return null;

  return (
    <div className="mt-2 px-3 py-2 bg-orange-50/60 border border-orange-100 rounded-lg">
      <div className="flex items-center gap-1 mb-1.5">
        <Flame size={11} className="text-orange-500" />
        <span className="text-xs font-semibold text-orange-700">Top Supporters</span>
      </div>
      <ol className="space-y-0.5">
        {supporters.map((s, i) => (
          <li key={s.user_id} className="flex items-center gap-1.5 text-xs text-orange-800">
            <span className="font-bold text-orange-500 w-3">{i + 1}.</span>
            <span className="font-medium truncate max-w-[120px]">{s.display_name}</span>
            <span className="text-orange-500 font-semibold ml-auto">+{s.support_count}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
